import React, { useEffect, useMemo, useState } from 'react';
import { DocumentDetailBarplot } from './DocumentDetailBarplot';
import { DocumentStatusMatrix } from './DocumentStatusMatrix';
import { parseAPIResponse } from './data-adapter';
import { DetailStatusRow, DocumentStatusMatrixRow, IndexStatus, STATUS_FIELDS, ThemeColors } from './types';
import { WidgetCard } from './WidgetCard';

// API configuration - uses relative URL for same-origin requests
const API_BASE_URL = '/api';

type DashboardData = {
    widgets: {
        totalDocumentos: number;
        totalIndexado: number;
        totalNoIndexado: number;
        totalEnProgreso: number;
        totalListoParaIndexar: number;
        totalCorrompido: number;
        totalFaltaOcr: number;
        totalPdfValidado: number;
        totalNoDescargado: number;
    };
    data: IndexStatus[];
    detailData: DetailStatusRow[];
    recentlyIndexed: { name: string; count: number }[];
};

const createDefaultDashboardState = (): DashboardData => ({
    widgets: {
        totalDocumentos: 0,
        totalIndexado: 0,
        totalNoIndexado: 0,
        totalEnProgreso: 0,
        totalListoParaIndexar: 0,
        totalCorrompido: 0,
        totalFaltaOcr: 0,
        totalPdfValidado: 0,
        totalNoDescargado: 0
    },
    data: [],
    detailData: [],
    recentlyIndexed: []
});

// Theme configuration
type Theme = 'light' | 'dark';

const getThemeColors = (theme: Theme): ThemeColors => {
    if (theme === 'light') {
        return {
            background: '#f9fafb',
            cardBackground: 'white',
            cardBorder: '#f0f0f0',
            text: '#111827',
            textSecondary: '#6b7280',
            buttonText: '#374151',
            buttonBackground: 'transparent',
            buttonHover: '#f3f4f6',
            buttonActiveBg: '#7c3aed',
            buttonActiveText: 'white',
            purple: '#8b5cf6',
            purpleDark: '#7c3aed',
            purpleLight: '#f0f0ff',
            border: '#e5e7eb',
            borderLight: '#f0f0f0',
            gridLine: '#f0f0f0',
            dropdownBg: 'white',
            dropdownBorder: '#d1d5db',
            dropdownHover: '#f9fafb',
            dropdownSelected: '#f0f0ff'
        };
    } else {
        return {
            background: '#212121',
            cardBackground: '#2d2d2d',
            cardBorder: '#404040',
            text: '#ffffff',
            textSecondary: '#b0b0b0',
            buttonText: '#e0e0e0',
            buttonBackground: 'transparent',
            buttonHover: '#404040',
            buttonActiveBg: '#8b5cf6',
            buttonActiveText: 'white',
            purple: '#a78bfa',
            purpleDark: '#8b5cf6',
            purpleLight: '#6d28d9',
            border: '#404040',
            borderLight: '#4a4a4a',
            gridLine: '#404040',
            dropdownBg: '#2d2d2d',
            dropdownBorder: '#4a4a4a',
            dropdownHover: '#404040',
            dropdownSelected: '#4c1d95'
        };
    }
};

export function Dashboard() {
    // Theme state - start in dark mode
    const [theme, setTheme] = React.useState<Theme>('dark');
    const themeColors = getThemeColors(theme);
    const successColor = theme === 'light' ? '#16a34a' : '#22c55e';

    // Data loading state
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [dashboardData, setDashboardData] = useState<DashboardData>(createDefaultDashboardState);
    const [selectedDocumentType, setSelectedDocumentType] = useState<string>('');
    const [detailData, setDetailData] = useState<DetailStatusRow[]>([]);

    // Fetch data from API on mount
    useEffect(() => {
        const fetchData = async () => {
            try {
                setIsLoading(true);
                setError(null);

                const response = await fetch(`${API_BASE_URL}/indexing-data`);

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const apiResponse = await response.json();
                const parsedData = parseAPIResponse(apiResponse);

                console.log('Loaded data from API:', parsedData);
                setDashboardData(parsedData);
            } catch (err) {
                console.error('Error fetching data:', err);
                setError(err instanceof Error ? err.message : 'Failed to load data');
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, []);

    const matrixData = useMemo(() => {
        const rows = dashboardData.data || [];
        const groupedByIndex = new Map<string, IndexStatus[]>();

        rows.forEach((row) => {
            const indexName = row.index_name || row.tipo_documento || 'Sin indice';
            if (!groupedByIndex.has(indexName)) {
                groupedByIndex.set(indexName, []);
            }
            groupedByIndex.get(indexName)?.push(row);
        });

        const buildSummaryRow = (label: string, items: IndexStatus[], id: string): DocumentStatusMatrixRow => {
            const summary = { tipo_documento: label, id } as DocumentStatusMatrixRow;
            STATUS_FIELDS.forEach(({ key }) => {
                summary[key] = items.reduce((sum, item) => sum + ((item[key as keyof IndexStatus] as number) || 0), 0);
            });
            return summary;
        };

        const matrixRows: DocumentStatusMatrixRow[] = [];

        groupedByIndex.forEach((items, indexName) => {
            const groupedByTipo = new Map<string, IndexStatus[]>();
            items.forEach((item) => {
                const tipo = item.tipo_documento || indexName;
                if (!groupedByTipo.has(tipo)) {
                    groupedByTipo.set(tipo, []);
                }
                groupedByTipo.get(tipo)?.push(item);
            });

            const children = Array.from(groupedByTipo.entries())
                .map(([tipo, childItems]) => buildSummaryRow(tipo, childItems, `child:${indexName}:${tipo}`))
                .sort((a, b) => a.tipo_documento.localeCompare(b.tipo_documento));

            const parent = buildSummaryRow(indexName, items, `parent:${indexName}`);
            parent.children = children;
            matrixRows.push(parent);
        });

        return matrixRows.sort((a, b) => a.tipo_documento.localeCompare(b.tipo_documento));
    }, [dashboardData.data]);

    const documentTypeOptions = useMemo(() => {
        const unique = new Set<string>();
        (dashboardData.data || []).forEach(row => {
            if (row.index_name) {
                unique.add(row.index_name);
            }
        });
        return Array.from(unique).sort((a, b) => a.localeCompare(b));
    }, [dashboardData.data]);

    useEffect(() => {
        if (selectedDocumentType && !documentTypeOptions.includes(selectedDocumentType)) {
            setSelectedDocumentType('');
        }
    }, [documentTypeOptions, selectedDocumentType]);

    useEffect(() => {
        setDetailData(dashboardData.detailData || []);
    }, [dashboardData.detailData]);

    const recentlyIndexed = useMemo(() => {
        return (dashboardData.recentlyIndexed || [])
            .filter((row) => (row.count || 0) > 0)
            .map((row) => ({
                ...row,
                name: row.name.replace(/-indexed$/i, '')
            }))
            .sort((a, b) => b.count - a.count);
    }, [dashboardData.recentlyIndexed]);

    const detailChartData = useMemo(() => {
        if (!selectedDocumentType) {
            return [];
        }
        const filtered = detailData.filter(row => row.index_name === selectedDocumentType);
        const aggregated = new Map<string, DetailStatusRow>();

        filtered.forEach(row => {
            const key = row.tipo_documento || selectedDocumentType;
            const existing = aggregated.get(key);
            if (!existing) {
                aggregated.set(key, { ...row, tipo_documento: key });
                return;
            }
            existing.total_documentos += row.total_documentos || 0;
            existing.indexado += row.indexado || 0;
            existing.corrompido += row.corrompido || 0;
            existing.falta_ocr += row.falta_ocr || 0;
            existing.pdf_validado += row.pdf_validado || 0;
        });

        return Array.from(aggregated.values()).sort((a, b) =>
            a.tipo_documento.localeCompare(b.tipo_documento)
        );
    }, [detailData, selectedDocumentType]);

    const calculatedWidgets = dashboardData.widgets;

    // Loading state
    if (isLoading) {
        return (
            <div style={{
                fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                backgroundColor: themeColors.background,
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: themeColors.text
            }}>
                <div style={{ textAlign: 'center' }}>
                    <div style={{
                        width: 48,
                        height: 48,
                        border: `4px solid ${themeColors.border}`,
                        borderTopColor: themeColors.purple,
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite',
                        margin: '0 auto 16px'
                    }} />
                    <p>Loading data...</p>
                    <style>{`
                        @keyframes spin {
                            to { transform: rotate(360deg); }
                        }
                    `}</style>
                </div>
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div style={{
                fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                backgroundColor: themeColors.background,
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: themeColors.text
            }}>
                <div style={{
                    textAlign: 'center',
                    padding: 32,
                    backgroundColor: themeColors.cardBackground,
                    borderRadius: 12,
                    border: `1px solid ${themeColors.cardBorder}`
                }}>
                    <div style={{ fontSize: 48, marginBottom: 16 }}>⚠️</div>
                    <h2 style={{ margin: '0 0 8px 0' }}>Error Loading Data</h2>
                    <p style={{ color: themeColors.textSecondary, margin: 0 }}>{error}</p>
                </div>
            </div>
        );
    }

    return (
        <div style={{
            fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            backgroundColor: themeColors.background,
            minHeight: '100vh',
            padding: '24px',
            position: 'relative'
        }}>

            <div style={{ maxWidth: 1400, margin: '0 auto' }}>
                {/* Header */}
                <div style={{
                    marginBottom: 16
                }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: 16,
                        marginBottom: 12
                    }}>
                        <div>
                            <h1 style={{
                                margin: 0,
                                fontSize: 28,
                                fontWeight: 700,
                                color: themeColors.text
                            }}>
                                Document Indexing Dashboard
                            </h1>
                            <p style={{
                                margin: '4px 0 0 0',
                                fontSize: 14,
                                color: themeColors.textSecondary
                            }}>
                                Document indexing status by type
                            </p>
                        </div>
                    </div>

                    {/* Theme Toggle */}
                    <div style={{
                        display: 'flex',
                        gap: 8,
                        backgroundColor: themeColors.cardBackground,
                        padding: 4,
                        borderRadius: 8,
                        border: `1px solid ${themeColors.border}`,
                        width: 'fit-content'
                    }}>
                        <button
                            onClick={() => setTheme('light')}
                            style={{
                                padding: '6px 14px',
                                borderRadius: 6,
                                border: 'none',
                                backgroundColor: theme === 'light' ? themeColors.buttonActiveBg : themeColors.buttonBackground,
                                color: theme === 'light' ? themeColors.buttonActiveText : themeColors.buttonText,
                                fontSize: 13,
                                fontWeight: 500,
                                cursor: 'pointer',
                                transition: 'all 0.2s'
                            }}
                        >
                            Claro
                        </button>
                        <button
                            onClick={() => setTheme('dark')}
                            style={{
                                padding: '6px 14px',
                                borderRadius: 6,
                                border: 'none',
                                backgroundColor: theme === 'dark' ? themeColors.buttonActiveBg : themeColors.buttonBackground,
                                color: theme === 'dark' ? themeColors.buttonActiveText : themeColors.buttonText,
                                fontSize: 13,
                                fontWeight: 500,
                                cursor: 'pointer',
                                transition: 'all 0.2s'
                            }}
                        >
                            Oscuro
                        </button>
                    </div>
                </div>

                {/* Widget Cards */}
                <div style={{
                    display: 'grid',
                    gap: 16,
                    marginTop: 24,
                    marginBottom: 24
                }}>
                    <WidgetCard
                        title="Documentos Totales"
                        value={calculatedWidgets.totalDocumentos}
                        icon="DOC"
                        size="lg"
                        themeColors={themeColors}
                    />
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                        gap: 16
                    }}>
                        <WidgetCard
                            title="Documentos Indexados"
                            value={calculatedWidgets.totalIndexado}
                            icon="OK"
                            size="md"
                            themeColors={themeColors}
                        />
                        <WidgetCard
                            title="Documentos No Indexados"
                            value={calculatedWidgets.totalNoIndexado}
                            icon="NO"
                            size="md"
                            themeColors={themeColors}
                        />
                    </div>
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                        gap: 16
                    }}>
                        <WidgetCard
                            title="No Indexados - Corrompidos"
                            value={calculatedWidgets.totalCorrompido}
                            icon="CORR"
                            themeColors={themeColors}
                        />
                        <WidgetCard
                            title="No Indexados - Falta OCR"
                            value={calculatedWidgets.totalFaltaOcr}
                            icon="OCR"
                            themeColors={themeColors}
                        />
                        <WidgetCard
                            title="No Indexados - Listo para indexar"
                            value={calculatedWidgets.totalListoParaIndexar}
                            icon="READY"
                            themeColors={themeColors}
                        />
                        <WidgetCard
                            title="No Indexados - No descargado"
                            value={calculatedWidgets.totalNoDescargado}
                            icon="NODL"
                            themeColors={themeColors}
                        />
                    </div>
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                        gap: 16
                    }}>
                        <WidgetCard
                            title="Documentos en progreso"
                            value={calculatedWidgets.totalEnProgreso}
                            icon="PROG"
                            size="md"
                            themeColors={themeColors}
                        />
                    </div>
                </div>

                {/* Detail Selector */}
                <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: 12,
                    marginBottom: 16
                }}>
                    <div style={{
                        fontSize: 13,
                        color: themeColors.textSecondary,
                        fontWeight: 600
                    }}>
                        Detalle por tipo de documento
                    </div>
                    <select
                        value={selectedDocumentType}
                        onChange={(event) => setSelectedDocumentType(event.target.value)}
                        style={{
                            padding: '8px 12px',
                            borderRadius: 8,
                            border: `1px solid ${themeColors.border}`,
                            backgroundColor: themeColors.cardBackground,
                            color: themeColors.text,
                            fontSize: 13,
                            fontWeight: 500,
                            minWidth: 240
                        }}
                    >
                        <option value="" disabled>
                            Selecciona un tipo
                        </option>
                        {documentTypeOptions.map(option => (
                            <option key={option} value={option}>
                                {option}
                            </option>
                        ))}
                    </select>
                    {selectedDocumentType && (
                        <button
                            onClick={() => setSelectedDocumentType('')}
                            style={{
                                padding: '8px 12px',
                                borderRadius: 8,
                                border: `1px solid ${themeColors.border}`,
                                backgroundColor: themeColors.cardBackground,
                                color: themeColors.text,
                                fontSize: 13,
                                fontWeight: 600,
                                cursor: 'pointer'
                            }}
                        >
                            Volver al dashboard
                        </button>
                    )}
                </div>

                {/* Charts */}
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr',
                    gap: 20
                }}>
                    {selectedDocumentType && detailChartData.length > 0 && (
                        <>
                            <div style={{
                                backgroundColor: themeColors.cardBackground,
                                borderRadius: 12,
                                padding: 16,
                                border: `1px solid ${themeColors.cardBorder}`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                color: themeColors.text
                            }}>
                                <div style={{ fontSize: 13, color: themeColors.textSecondary }}>
                                    Total documentos únicos ({selectedDocumentType})
                                </div>
                                <div style={{ fontSize: 20, fontWeight: 700 }}>
                                    {detailChartData.reduce((sum, row) => sum + (row.total_documentos || 0), 0)}
                                </div>
                            </div>
                            <DocumentDetailBarplot
                                title={`Detalle por tipo_documento - ${selectedDocumentType}`}
                                data={detailChartData}
                                height={Math.max(300, detailChartData.length * 50 + 100)}
                                themeColors={themeColors}
                            />
                        </>
                    )}
                    {!selectedDocumentType && (
                        <DocumentStatusMatrix
                            title="Document Status Matrix"
                            data={matrixData}
                            themeColors={themeColors}
                        />
                    )}
                </div>

                {/* Recently Indexed */}
                <div style={{
                    marginTop: 20,
                    backgroundColor: themeColors.cardBackground,
                    borderRadius: 12,
                    padding: 16,
                    border: `1px solid ${themeColors.cardBorder}`
                }}>
                    <div style={{
                        fontSize: 14,
                        fontWeight: 700,
                        color: themeColors.text,
                        marginBottom: 12
                    }}>
                        Últimos indexados (7 días)
                    </div>
                    {recentlyIndexed.length === 0 && (
                        <div style={{
                            fontSize: 13,
                            color: themeColors.textSecondary
                        }}>
                            Sin documentos recientes.
                        </div>
                    )}
                    {recentlyIndexed.length > 0 && (
                        <div style={{
                            display: 'grid',
                            gap: 8
                        }}>
                            {recentlyIndexed.map((row) => (
                                <div
                                    key={row.name}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        padding: '8px 10px',
                                        borderRadius: 8,
                                        border: `1px solid ${themeColors.borderLight}`,
                                        backgroundColor: themeColors.background
                                    }}
                                >
                                    <div style={{
                                        fontSize: 13,
                                        color: themeColors.text,
                                        fontWeight: 500
                                    }}>
                                        {row.name}
                                    </div>
                                    <div style={{
                                        fontSize: 13,
                                        fontWeight: 700,
                                        color: successColor
                                    }}>
                                        {row.count.toLocaleString()}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default Dashboard;


